import { z } from 'zod';

// The contract for GET /v1/public/events/:idOrSlug/site (design §7.3, T9).
//
// The load-bearing rule of this whole file: a hidden section's key is ABSENT
// from the payload, and an empty array means the section is visible with
// nothing in it yet. Those are two different screens — "the organisers have not
// published this" versus "nothing is scheduled yet" — and the only thing that
// tells them apart is whether the key arrived. Every optional() below is
// therefore a plain optional() with no .default([]): a default would erase the
// distinction at the parse boundary, before any component could act on it.

const nullableText = z
  .string()
  .nullish()
  .transform((v) => v ?? '');

export const SiteEventSchema = z.object({
  name: z.string(),
  slug: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  timezone: nullableText,
  location: nullableText,
  description: nullableText,
});

// Only `id` and `title` are required. The rest of the DTO is an allowlist the
// backend chooses (T9), and a session that arrives without a room should render
// without a room — not fail the whole schedule to a parse error.
export const SiteSessionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: nullableText,
  startAt: z.string().nullish(),
  endAt: z.string().nullish(),
  room: z.string().nullish(),
  trackId: z.string().nullish(),
  type: z.string().nullish(),
  speakerIds: z.array(z.string()).optional(),
});

export const SiteTrackSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullish(),
});

export const SiteSpeakerSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string().nullish(),
  company: z.string().nullish(),
  bio: nullableText,
  photo: z.string().nullish(),
  photoUrl: z.string().nullish(),
});

export const SiteSponsorSchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: z.string().nullish(),
  description: nullableText,
  logoUrl: z.string().nullish(),
  websiteUrl: z.string().nullish(),
});

/** One of the organiser's sponsor tiers: the key sponsors carry in `tier`, and its label. */
export const SiteSponsorTierSchema = z.object({
  key: z.string(),
  label: z.string(),
  sortOrder: z.number().int(),
});

export interface SponsorTierGroup {
  key: string;
  label: string;
  sponsors: SiteSponsor[];
}

/** "media-partner" → "Media Partner": the backend's label for a tier it was never told the name of. */
export const labelFromTierKey = (key: string): string => {
  const words = key
    .replace(/[-_\s]+/g, ' ')
    .trim()
    .split(' ')
    .filter((w) => w !== '')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  return words.length > 0 ? words.join(' ') : key;
};

/**
 * Sponsors under the organiser's tier headings, in the backend's tier order.
 *
 * Null when the payload carries no `sponsorTiers` (an older backend): the page
 * then renders the flat list it always did. A sponsor whose tier is not in the
 * list is appended under a heading made from its key — never dropped. The
 * backend already sends sponsors in display order, so order within a heading
 * is kept as received.
 */
export const groupSponsorsByTier = (
  sponsors: readonly SiteSponsor[],
  tiers: readonly SiteSponsorTier[] | undefined,
): SponsorTierGroup[] | null => {
  if (!tiers) return null;
  const byKey = new Map<string, SiteSponsor[]>();
  for (const s of sponsors) {
    const key = s.tier ?? '';
    byKey.set(key, [...(byKey.get(key) ?? []), s]);
  }
  const known = new Set(tiers.map((t) => t.key));
  const groups: SponsorTierGroup[] = [...tiers]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((t) => ({ key: t.key, label: t.label, sponsors: byKey.get(t.key) ?? [] }));
  const unknown = [...byKey.keys()].filter((k) => !known.has(k)).sort();
  for (const key of unknown) {
    groups.push({
      key,
      label: key === '' ? 'Other' : labelFromTierKey(key),
      sponsors: byKey.get(key) ?? [],
    });
  }
  return groups.filter((g) => g.sponsors.length > 0);
};

export const VISIBILITY_SECTIONS = [
  'schedule',
  'speakers',
  'sponsors',
  'attendees',
  'photos',
  'announcements',
] as const;
export type VisibilitySection = (typeof VISIBILITY_SECTIONS)[number];

export const VisibilitySchema = z
  .object({
    schedule: z.boolean(),
    speakers: z.boolean(),
    sponsors: z.boolean(),
    attendees: z.boolean(),
    photos: z.boolean(),
    announcements: z.boolean(),
  })
  .partial();

export const ComingSoonCopySchema = z.object({
  title: z.string(),
  body: z.string(),
});

export const SitePayloadSchema = z.object({
  event: SiteEventSchema,
  theme: z.unknown().optional(),
  visibility: VisibilitySchema.default({}),
  comingSoonCopy: ComingSoonCopySchema.optional(),
  sessions: z.array(SiteSessionSchema).optional(),
  tracks: z.array(SiteTrackSchema).optional(),
  speakers: z.array(SiteSpeakerSchema).optional(),
  sponsors: z.array(SiteSponsorSchema).optional(),
  // Present with `sponsors` from a backend that manages tiers; absent before.
  sponsorTiers: z.array(SiteSponsorTierSchema).optional(),
});

export type SiteEvent = z.infer<typeof SiteEventSchema>;
export type SiteSession = z.infer<typeof SiteSessionSchema>;
export type SiteTrack = z.infer<typeof SiteTrackSchema>;
export type SiteSpeaker = z.infer<typeof SiteSpeakerSchema>;
export type SiteSponsor = z.infer<typeof SiteSponsorSchema>;
export type SiteSponsorTier = z.infer<typeof SiteSponsorTierSchema>;
export type SitePayload = z.infer<typeof SitePayloadSchema>;

/** The wording for a hidden section, when the backend did not send its own. */
export const DEFAULT_COMING_SOON = {
  title: 'Event details coming soon',
  body: 'We are putting the finishing touches on this. Check back shortly.',
} as const;

export type SectionState<T> =
  | { kind: 'hidden' }
  | { kind: 'empty' }
  | { kind: 'ready'; items: T[] };

/**
 * Which of the three screens a section gets.
 *
 * `undefined` — the key never arrived — is hidden. `null` is treated the same
 * way: it would be a contract slip, and of the two possible readings, "the
 * organisers have not published this" is the one that cannot embarrass them.
 */
export const sectionState = <T>(items: readonly T[] | null | undefined): SectionState<T> => {
  if (items === undefined || items === null) return { kind: 'hidden' };
  return items.length === 0 ? { kind: 'empty' } : { kind: 'ready', items: [...items] };
};

/** The coming-soon wording for a payload, backend copy winning over ours. */
export const comingSoonCopy = (payload: SitePayload | null) =>
  payload?.comingSoonCopy ?? DEFAULT_COMING_SOON;

/**
 * Sessions grouped by local day, days and sessions both in time order.
 *
 * Sessions with no start time sort last within their group and land in a
 * trailing "Time to be confirmed" group, because a schedule that drops them
 * silently is worse than one that admits a gap.
 */
export interface SessionDay {
  /** The `YYYY-MM-DD` key in the event's timezone, or null for undated sessions. */
  key: string | null;
  sessions: SiteSession[];
}

export const groupSessionsByDay = (sessions: SiteSession[], timezone: string): SessionDay[] => {
  const days = new Map<string, SiteSession[]>();
  const undated: SiteSession[] = [];

  for (const session of sessions) {
    const key = dayKey(session.startAt, timezone);
    if (!key) {
      undated.push(session);
      continue;
    }
    const bucket = days.get(key);
    if (bucket) bucket.push(session);
    else days.set(key, [session]);
  }

  const ordered: SessionDay[] = [...days.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, list]) => ({
      key,
      sessions: [...list].sort((a, b) => Date.parse(a.startAt ?? '') - Date.parse(b.startAt ?? '')),
    }));

  if (undated.length > 0) ordered.push({ key: null, sessions: undated });
  return ordered;
};

const dayKey = (iso: string | null | undefined, timezone: string): string | null => {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    // en-CA gives YYYY-MM-DD, which sorts as a string — the reason for the
    // locale choice, not a preference about Canada.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(at);
  } catch {
    return at.toISOString().slice(0, 10);
  }
};
